import { i18n } from '@/i18n/settings'

/**
 * Date and time of the event windows, written with the i18n formatters (`d`).
 *
 * The event dates are UTC on purpose: the cron aligns the cycles to a weekday in UTC and the
 * window ends at 23:59:59 UTC. Rendering them with the browser timezone would show that instant
 * as the following day for every user east of Greenwich (23:59:59Z = 01:59:59 in Italy), so the
 * day is always read in UTC and the players see the last day they can actually race.
 *
 * A date is the day, the abbreviated month and the year ("10 Ott 2026"), in the language of the
 * page: the month is a word, and which word it is is the language. Plain `en` puts it in front
 * ("Oct 10, 2026"), so English is written with en-GB.
 */

/** Locale each language writes its dates with (see the note above). */
const DATE_LOCALES = { en: 'en-GB', it: 'it' }

/**
 * One instant written with the options it is asked for — the whole date, or the day alone, or the
 * day and the month (see DateRange).
 */
function writeDate(iso, options)
{
  const locale = DATE_LOCALES[i18n.global.locale.value] ?? DATE_LOCALES.en
  const text = i18n.global.d(new Date(iso), { timeZone: 'UTC', ...options }, locale)

  // Intl writes Italian months lowercase ("10 ott 2026"); the site starts them with a capital
  // letter, the way "10 Oct 2026" reads and the tables write them.
  return text.replace(/\p{L}+/u, (word) => word[0].toUpperCase() + word.slice(1))
}

/** Date of an event window: "10 Ott 2026". */
export function formatDate(iso)
{
  return iso ? writeDate(iso, { day: 'numeric', month: 'short', year: 'numeric' }) : '–'
}

/** Day on its own ("1"), for the end of a period that shares all the rest (see DateRange). */
export function formatDay(iso)
{
  return iso ? writeDate(iso, { day: 'numeric' }) : '–'
}

/** Day and abbreviated month, without the year ("10 Ott"), for a period inside one year. */
export function formatDayMonth(iso)
{
  return iso ? writeDate(iso, { day: 'numeric', month: 'short' }) : '–'
}

/**
 * Time of day of an event deadline, always in UTC and always spelled out: the players have to be
 * able to read "23:59 UTC" and know that it is not their own midnight (see formatDate above).
 */
export function formatUtcTime(iso)
{
  if (!iso) {
    return '–'
  }

  const options = { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: false }

  return `${i18n.global.d(new Date(iso), options)} UTC`
}

/**
 * Date and time of an instant, both read in UTC like the rest of the site: a race is a moment, not
 * a day, and the players have to be able to compare it with the event windows and with each other.
 */
export function formatDateTime(iso)
{
  return iso ? `${formatDate(iso)} ${formatUtcTime(iso)}` : '–'
}

/**
 * Name of the month a date falls in, in the language of the page: it is a word of the event's title
 * ("Championship of September"), not a date, so it follows the page and not the date format.
 */
export function getMonthName(iso)
{
  return iso ? i18n.global.d(new Date(iso), { timeZone: 'UTC', month: 'long' }) : '–'
}
